import {
  ApolloClient,
  InMemoryCache,
  HttpLink,
  ApolloLink,
} from '@apollo/client';

// Apollo deep-freezes every query result unless `globalThis.__DEV__` is
// explicitly `false`, relying on the bundler to substitute `__DEV__` for a
// literal in production builds. Webpack did that; Next 16's default Turbopack
// build does not, so the flag stays `undefined` (`undefined !== false`) and
// Apollo freezes results in production. Any in-place mutation of a result array
// downstream — an `Array.prototype.sort` in a transform or render path — then
// throws "Cannot assign to read only property '0' of object '[object Array]'"
// and 500s the page.
//
// Apollo reads the flag at call time (`maybeDeepFreeze` checks
// `globalThis.__DEV__ !== false` on every result), so setting it here — after
// the import, before any query runs — is sufficient; it does not depend on
// module evaluation order.
//
// This FORCES the flag in production rather than only defaulting it when
// undefined: on some builds (Vercel, restored build cache) `__DEV__` is already
// defined as truthy, so a `typeof === 'undefined'` guard would skip and leave
// freezing enabled — which is how the sibling project stayed clean locally but
// still 500'd in production.
if (process.env.NODE_ENV === 'production') {
  globalThis.__DEV__ = false;
} else if (typeof globalThis.__DEV__ === 'undefined') {
  globalThis.__DEV__ = true;
}

// Without this the URI silently becomes "undefined/graphql" and every query
// fails with an opaque network error.
if (!process.env.NEXT_PUBLIC_WORDPRESS_URL) {
  console.error(
    '[apolloClient] NEXT_PUBLIC_WORDPRESS_URL is not set — every GraphQL request will fail.'
  );
}

// A hung CMS must not hold a request open indefinitely.
const REQUEST_TIMEOUT_MS = 20_000;

const errorLoggingLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    if (response.errors?.length) {
      console.error(
        `[GraphQL errors] operation="${operation.operationName}"`,
        response.errors
      );
    }
    return response;
  });
});

const client = new ApolloClient({
  link: ApolloLink.from([
    errorLoggingLink,
    new HttpLink({
      uri: process.env.NEXT_PUBLIC_WORDPRESS_URL + '/graphql',
      fetch: (uri, options) => {
        return fetch(uri, {
          ...options,
          signal: options?.signal ?? AbortSignal.timeout(REQUEST_TIMEOUT_MS),
        });
      },
    }),
  ]),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'no-cache',
      // 'all', not 'ignore': with 'ignore' the `error` returned by every
      // useQuery hook was permanently undefined, so no client-side GraphQL
      // failure could ever be detected or logged.
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    },
  },
});

export default client;
